/**
 * THE PLACEHOLDERS IN A NOTIFICATION TEMPLATE, and what to show in their place.
 *
 * The sender (`Coeia.Rawam.Jobs`, `NotificationHelper.ReplacePlaceHolders`) swaps each
 * `__Token` for a real value as the message goes out. A preview has no real value, so it
 * swaps in a sample instead — this is where those samples live.
 */

/**
 * WHAT THE RECIPIENT WOULD SEE where the template says `__Course_EN`.
 *
 * The values are stand-ins, chosen to read as content rather than as codes so the layout
 * can be judged — and to be recognisably invented, so nobody mistakes the preview for a
 * real message. Keys are matched case-insensitively: the stored templates spell a few of
 * them differently from the sender's catalogue (`__Username` and `__USERNAME`,
 * `__Assessment_End_Date` and `__Assessment_End_date`), and the preview is for judging
 * the design, not for auditing which spelling the sender will honour.
 */
/**
 * The direction the template itself declares. Most are `<html lang="en">` with a Latin
 * column on the left and an Arabic one on the right; edited on an RTL root those columns
 * swap, and the author formats a mirror image of what will be sent. A document that says
 * `dir="rtl"` on its `<html>` or `<body>` is edited that way; anything else is LTR, which
 * is what a mail client does with an undeclared document too.
 */
export function documentDirection(html) {
  return /<(?:html|body)\b[^>]*\bdir\s*=\s*["']?rtl/i.test(html ?? '') ? 'rtl' : 'ltr'
}

/**
 * The ground the template paints its `<body>` — `style="background-color: #f4f4f4"` or a
 * `bgcolor` attribute. The editor cannot apply body styles inside its editable, so the
 * page reads this and paints the editable the same colour: the message is composed on
 * the ground it will be read on, and the compose view agrees with the sample view. Null
 * when the template declares none.
 */
export function documentGround(html) {
  const body = /<body\b[^>]*>/i.exec(html ?? '')?.[0]
  if (!body) return null
  const styled = /background(?:-color)?\s*:\s*([^;"']+)/i.exec(body)?.[1]
  const attr = /\bbgcolor\s*=\s*["']?([^"'\s>]+)/i.exec(body)?.[1]
  const value = (styled ?? attr ?? '').trim()
  return /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|[a-z]+)$/i.test(value) ? value : null
}

export const SAMPLE_VALUES = {
  __USERNAME: 'Sara Al-Otaibi',
  __Password: 'Rw-7pQ2x',
  __SEND_DATE: new Date().toLocaleDateString('en-GB'),
  __App_Name_EN__: 'Rawam',
  __App_Name_AR: 'روام',
  __App_URL: 'https://rawam.example',
  __URL: 'https://rawam.example',
  __Invitation_URL: 'https://rawam.example/Signup?invitation=sample',
  __ResetPassword_URL: 'https://rawam.example/ResetPassword?token=sample',
  __Course_EN: 'Phishing Awareness Essentials',
  __Course_AR: 'أساسيات الوعي بالتصيد',
  __Course_Start: '01/10/2026 09:00',
  __Course_End: '31/10/2026 17:00',
  __Course_URL: 'https://rawam.example/Courses/Sample',
  __Program_EN: 'Annual Security Awareness Program',
  __Program_AR: 'برنامج التوعية الأمنية السنوي',
  __Program_Start: '01/10/2026',
  __Program_End: '31/12/2026',
  __Program_URL: 'https://rawam.example/Programs/Sample',
  __Assessment_EN: 'Q4 Security Knowledge Check',
  __Assessment_AR: 'اختبار المعرفة الأمنية للربع الرابع',
  __Assessment_Start_Date: '05/10/2026 08:00',
  __Assessment_End_Date: '12/10/2026 23:59',
  __Survey_EN: 'Awareness Program Feedback',
  __Survey_AR: 'استبيان تقييم برنامج التوعية',
  __Survey_URL: 'https://rawam.example/Surveys/Sample',
  __Policy_EN: 'Acceptable Use Policy',
  __Policy_AR: 'سياسة الاستخدام المقبول',
  __Policy_URL: 'https://rawam.example/Policies/Sample',
  __Awareness_EN: 'Spotting a Suspicious Email',
  __Awareness_AR: 'كيف تكتشف رسالة مشبوهة',
  __Awareness_URL: 'https://rawam.example/Awareness/Sample',
  __SecReport_ID: '1042',
  __SecReport_Title: 'Suspicious sign-in attempt',
  __SecReport_State_EN: 'Under review',
  __SecReport_State_AR: 'قيد المراجعة',
  __SecReport_URL: 'https://rawam.example/SecurityReports/1042',
  __PAGE_TITLE_EN: 'Notification',
  __PAGE_TITLE_AR: 'إشعار',
  __MESSAGE_BODY: 'This is a sample message body, shown here so the layout can be judged.',
}

const PLACEHOLDER = /__[A-Za-z]+(?:_[A-Za-z]+)*_{0,2}/g

/* Longest first, so `__App_Name_EN__` is not half-eaten by a shorter key. */
const SAMPLE_ENTRIES = Object.entries(SAMPLE_VALUES)
  .map(([key, value]) => [key.toLowerCase(), value])
  .sort(([a], [b]) => b.length - a.length)

/** The placeholders a template mentions, in the order first met, each at most once. */
export function findPlaceholders(html) {
  const seen = new Set()
  for (const match of (html ?? '').matchAll(PLACEHOLDER)) {
    seen.add(match[0])
  }
  return [...seen]
}

/** The sample value for one placeholder, or null when it has none. */
export function sampleFor(placeholder) {
  const key = placeholder.toLowerCase()
  return SAMPLE_ENTRIES.find(([candidate]) => candidate === key)?.[1] ?? null
}

/**
 * The template with every known placeholder swapped for its stand-in. An unknown one is
 * left exactly as written — visible in the preview, and listed as unknown in the legend —
 * which is the honest thing to show for a token the sender will not replace either.
 */
export function fillPlaceholders(html) {
  return (html ?? '').replace(PLACEHOLDER, (token) => sampleFor(token) ?? token)
}
